import React, { useRef, useState } from 'react'
import moment, { Moment } from 'moment-timezone'
import { Select as MuiSelect, MenuItem, FormControl } from '@mui/material'
import TenantDatePicker from '../../shared/TenantDatePicker'
import {
  DEFAULT_LOCALIZATION_CONTEXT,
  LocalizationContext,
  LocalizationContextType
} from '../../shared/contexts'
import { admin_alert_automation_audit_logs_path } from '../../../../routes'
import { AuditLogFilterValues, BusinessErrorFilterOption, ScenarioOption } from './types'

interface AuditLogFiltersProps {
  scenarios: ScenarioOption[]
  business_errors: BusinessErrorFilterOption[]
  current_filters: AuditLogFilterValues
  localization_context?: LocalizationContextType
}

const DATE_PARAM_FORMAT = 'YYYY-MM-DD'

const selectSx = {
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#dee2e6' }
}
const datePickerTextFieldProps = { size: 'small' as const, fullWidth: true }

const dateValue = (value?: string | null): Moment | null => (value ? moment(value, DATE_PARAM_FORMAT) : null)

interface FilterSelectProps {
  id: string
  label: string
  allLabel: string
  value?: string | null
  options: { value: string, label: string }[]
  onChange: (value: string) => void
}

const FilterSelect = ({ id, label, allLabel, value, options, onChange }: FilterSelectProps) => (
  <div className="col-md-3">
    <label className="form-label" id={id}>{label}</label>
    <FormControl fullWidth size="small">
      <MuiSelect
        labelId={id}
        value={value || ''}
        onChange={(event) => onChange(event.target.value)}
        displayEmpty
        sx={selectSx}
      >
        <MenuItem value="">{allLabel}</MenuItem>
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
        ))}
      </MuiSelect>
    </FormControl>
  </div>
)

interface DateFilterProps {
  id: string
  label: string
  value?: string | null
  format: string
  minDate?: Moment
  maxDate?: Moment
  onApply: (value: string | null) => void
}

// The picker reports every keystroke; a half-typed date is not a filter yet. Editing one section of
// a filled date already reads as a valid one (a "2" typed into the year is 0002), so the date is
// applied only once it is picked from the calendar or the field is left.
const DateFilter = ({ id, label, value, format, minDate, maxDate, onApply }: DateFilterProps) => {
  const [draft, setDraft] = useState<Moment | null>(() => dateValue(value))
  const applied = useRef<string | null>(value || null)

  const commit = (date: Moment | null) => {
    if (date && !date.isValid()) return

    const next = date ? date.format(DATE_PARAM_FORMAT) : null
    if (next === applied.current) return

    applied.current = next
    onApply(next)
  }

  return (
    <div className="col-md-2">
      <label className="form-label" htmlFor={id}>{label}</label>
      <TenantDatePicker
        value={draft}
        onChange={setDraft}
        onAccept={commit}
        format={format}
        minDate={minDate}
        maxDate={maxDate}
        slotProps={{ textField: { ...datePickerTextFieldProps, id, onBlur: () => commit(draft) } }}
      />
    </div>
  )
}

// Each filter is its own server-rendered query, so a change navigates instead of refetching - the
// same as the other admin logs, and what keeps the pagination links and the URL honest.
const AuditLogFilters = ({
  scenarios,
  business_errors: businessErrors,
  current_filters: currentFilters,
  localization_context: localizationContext = DEFAULT_LOCALIZATION_CONTEXT
}: AuditLogFiltersProps) => {
  const apply = (changes: AuditLogFilterValues) => {
    const params = new URLSearchParams()
    Object.entries({ ...currentFilters, ...changes }).forEach(([key, value]) => {
      if (value) params.append(key, value)
    })

    const query = params.toString()
    window.location.href = `${admin_alert_automation_audit_logs_path()}${query ? `?${query}` : ''}`
  }

  const hasFilters = Object.values(currentFilters).some(Boolean)
  const dateFormat = localizationContext.date_format

  return (
    <LocalizationContext.Provider value={localizationContext}>
      <div className="card mb-3">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <FilterSelect
              id="audit-log-scenario-label"
              label="Scenario"
              allLabel="All scenarios"
              value={currentFilters.scenario_id}
              options={scenarios.map(({ id, name }) => ({ value: String(id), label: name }))}
              onChange={(value) => apply({ scenario_id: value })}
            />

            <FilterSelect
              id="audit-log-error-label"
              label="Error"
              allLabel="All errors"
              value={currentFilters.business_error_code}
              options={businessErrors.map(({ code, description }) => ({ value: code, label: `${code} · ${description}` }))}
              onChange={(value) => apply({ business_error_code: value })}
            />

            <DateFilter
              id="audit-log-start-date"
              label="Start Date"
              value={currentFilters.start_date}
              format={dateFormat}
              maxDate={dateValue(currentFilters.end_date) || undefined}
              onApply={(date) => apply({ start_date: date })}
            />

            <DateFilter
              id="audit-log-end-date"
              label="End Date"
              value={currentFilters.end_date}
              format={dateFormat}
              minDate={dateValue(currentFilters.start_date) || undefined}
              onApply={(date) => apply({ end_date: date })}
            />

            <div className="col-md-2">
              <button
                type="button"
                className="btn btn-outline-secondary"
                disabled={!hasFilters}
                onClick={() => { window.location.href = admin_alert_automation_audit_logs_path() }}
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      </div>
    </LocalizationContext.Provider>
  )
}

export default AuditLogFilters
